import { DisputeFormProps } from '@anm/api/modules/stocks';
import Tooltip from '@anm/components/Tooltip';
import ButtonLoader from '@anm/components/buttons/ButtonLoader';
import { InfoLabel } from '@anm/styles/helpers';
import { Config as FinalFormConfig } from 'final-form';
import { useCallback, FC, SyntheticEvent } from 'react';
import { Form, FormRenderProps } from 'react-final-form';

import { EmailField, NumberField, RequiredTextField } from '../fields';
import { composeValidators, required, youtubeUrl } from '../validators';

import FormWrapper, {
  Fields,
  FieldGroup,
  RelativeBlock,
  TooltipText
} from './Wrapper';

export type YoutubeCopyrightClaimFormProps = {
  isPendingSubmit: boolean;
  onSubmit(values: DisputeFormProps): void;
};

const InfoTooltip: FC<{ htmlText: string }> = ({ htmlText }) => (
  <Tooltip
    withArrow
    delayHide={300}
    delayShow={300}
    direction="to-top"
    variant="black"
    renderContent={() => (
      <TooltipText dangerouslySetInnerHTML={{ __html: htmlText }} />
    )}
  >
    <InfoLabel size={18} />
  </Tooltip>
);

const renderForm = ({
  isPendingSubmit,
  onSubmit
}: YoutubeCopyrightClaimFormProps) => ({
  form,
  valid,
  values
}: FormRenderProps) => {
  const handleSubmit = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      onSubmit(values as DisputeFormProps);
      form.reset();
    },
    [values]
  );

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <Fields>
        <FieldGroup>
          <RequiredTextField
            label=""
            name="disputed_url"
            placeholder="YouTube URL"
            labelVariant="static-above"
            validate={composeValidators(required(), youtubeUrl)}
          />
          <RequiredTextField
            label=""
            name="disputer_name"
            placeholder="Your Name"
            labelVariant="static-above"
          />
          <RelativeBlock>
            <InfoTooltip
              htmlText={
                'You can find the stock ID in the inspector, ' +
                "<a target='_blank' href='https://wave.video/help/articles/3349377'>see how.</a>"
              }
            />
            <NumberField
              label=""
              name="stock_item_id"
              placeholder="Stock ID"
              labelVariant="static-above"
            />
          </RelativeBlock>
        </FieldGroup>

        <FieldGroup>
          <RelativeBlock>
            <InfoTooltip htmlText="Find this information on the YouTube claim." />
            <RequiredTextField
              label=""
              name="claimant_name"
              placeholder="Claimant Name"
              labelVariant="static-above"
            />
          </RelativeBlock>
          <EmailField
            autoFocus={false}
            name="disputer_email"
            placeholder="Your Email"
            labelVariant="static-above"
          />
        </FieldGroup>
      </Fields>

      <ButtonLoader
        size="large"
        color="gray"
        variant="blue-heavy-border"
        disabled={!valid}
        isPending={isPendingSubmit}
      >
        Submit dispute
      </ButtonLoader>
    </FormWrapper>
  );
};

const YoutubeCopyrightClaimForm: FC<FinalFormConfig &
  YoutubeCopyrightClaimFormProps> = props => (
  <Form render={renderForm(props)} {...props} />
);

export default YoutubeCopyrightClaimForm;
